import {useCartStore} from './use-cart-store';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {IconButton} from '@ui/buttons/icon-button';
import {CloseIcon} from '@ui/icons/material/Close';
import {AddIcon} from '@ui/icons/material/Add';
import {RemoveIcon} from '@ui/icons/material/Remove';
import {DeleteIcon} from '@ui/icons/material/Delete';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {CartItem} from './product';
import {apiClient} from '@common/http/query-client';
import {useState} from 'react';

interface CartDialogProps {
  localCurrency?: string;
  localCurrencyRate?: number;
}

export function CartDialog({localCurrency = "GBP", localCurrencyRate = 1}: CartDialogProps) {
  const cart = useCartStore((state: any) => state.cart);
  const updateQuantity = useCartStore((state: any) => state.updateQuantity);
  const removeItem = useCartStore((state: any) => state.removeItem);
  const getCartTotal = useCartStore((state: any) => state.getCartTotal);
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Use currency from first cart item if available, otherwise use prop
  const displayCurrency = cart.length > 0 && cart[0].localCurrency 
    ? cart[0].localCurrency 
    : localCurrency;

  const total = getCartTotal();

  // Currency symbol mapping
  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'GBP': return '£';
      case 'USD': return '$';
      case 'INR': return '₹';
      default: return currency;
    }
  };

  const handleCheckout = async () => {
    setIsProcessing(true);
    setError('');

    try {
      // Generate token for checkout
      const tokenResponse = await apiClient.post('drive/generate-token', {
        endpoint_type: 'products',
      });
      
      const token = tokenResponse.data.token;

      // Call external checkout API directly
      const selectedProducts = cart.map((item: CartItem) => ({
        id: item.id,
        quantity: item.quantity,
      }));

      const response = await fetch('https://apcrosa.syscomlive.co.uk/api/v1/drive/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token,
        },
        body: JSON.stringify({
          products: selectedProducts,
          from_drive:true
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const session = await response.json();
      
      window.location.href = session.id;
    } catch (error) {
      setError('Error creating checkout session, please try again later');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog size="lg" className="max-h-[90vh] overflow-hidden">
      <DialogHeader className="px-16 md:px-24 py-16">
        <Trans message="Shopping Cart" />
      </DialogHeader>
      <DialogBody className="px-16 md:px-24 pb-16 max-h-[calc(90vh-120px)] overflow-y-auto">
        {cart.length === 0 ? (
          <div className="text-center py-48 text-muted">
            <Trans message="Your cart is empty" />
          </div>
        ) : (
          <div className="space-y-16">
            {/* Cart Items */}
            <div className="space-y-12">
              {cart.map((item: CartItem) => {
                const price = parseFloat(item.price[0]?.price || '0');
                const originalCurrency = item.price[0]?.currency || 'GBP';
                const convertedPrice = item.localCurrencyRate ?? price;
                const itemTotal = convertedPrice * item.quantity;

                return (
                  <div
                    key={item.id}
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-12 p-12 rounded border border-divider"
                  >
                    {/* Product Image and Info */}
                    <div className="flex items-center gap-12 flex-1 w-full sm:w-auto">
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-40 h-40 md:w-48 md:h-48 object-contain rounded flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold mb-4">{item.name}</h4>
                        {item.storage && (
                          <p className="text-xs text-muted">
                            {item.storage} GB Storage
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Quantity Controls and Price */}
                    <div className="flex items-center justify-between w-full sm:w-auto sm:flex-col sm:items-end gap-8">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-8 order-2 sm:order-1">
                        <IconButton
                          size="sm"
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1)
                          }
                          disabled={item.quantity <= 1}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <span className="text-sm font-medium w-32 text-center">
                          {item.quantity}
                        </span>
                        <IconButton
                          size="sm"
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                          disabled={
                            item.qty?.max
                              ? item.quantity >= item.qty.max
                              : false
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      </div>

                      {/* Price and Remove */}
                      <div className="flex items-center gap-8 order-1 sm:order-2">
                        <div className="text-sm font-semibold w-64 text-center sm:text-right">
                          {getCurrencySymbol(displayCurrency)}
                          {itemTotal.toFixed(2)}
                        </div>
                        <IconButton
                          size="sm"
                          color="danger"
                          onClick={() => removeItem(item.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Cart Summary */}
            <div className="border-t border-divider pt-16">
              <div className="space-y-8">
                <div className="flex justify-between text-lg font-bold">
                  <span>
                    <Trans message="Total" />:
                  </span>
                  <span className="text-primary">{getCurrencySymbol(displayCurrency)}{(total).toFixed(2)}</span>
                </div>
              </div>

              {/* Checkout Button */}
              <div className="mt-24">
                {error && (
                  <div className="mb-12 p-12 bg-danger/10 border border-danger/20 rounded text-sm text-danger">
                    {error}
                  </div>
                )}
                <Button
                  variant="flat"
                  color="primary"
                  className="w-full"
                  size="md"
                  onClick={handleCheckout}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <Trans message="Processing..." />
                  ) : (
                    <Trans message="Proceed to Checkout" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogBody>
    </Dialog>
  );
}
